/**
 * Validation for the registration (`/registration`) and onboarding (`/register`)
 * bio forms.
 *
 * These checks used to live inside Vueform `Validator` subclasses declared in
 * the components themselves, which made them unreachable from a test: the rules
 * only ran once a whole `<Vueform>` had mounted and resolved its schema. The
 * screens now render design-system fields bound straight to a plain object, so
 * the rules live here as pure functions — same messages, same outcomes, testable
 * on their own.
 */
import { REGEX_EMAIL, REGEX_NAME, REGEX_NRIC } from '@/constants/validation/regex-patterns';
import {
  ERROR_GROUP,
  ERROR_PATTERN_VALID_NAME,
  ERROR_REQUIRED,
  ERROR_SELECT_OPTION,
  ERROR_VALID_DATE,
  ERROR_VALID_EMAIL,
  ERROR_VALID_FAMILY_NAME,
  ERROR_VALID_NRIC,
} from '@/constants/validation/error-messages';
import { resolveCgId } from '@/utils/cg-status';

export const ERROR_DATE_IN_FUTURE = 'Date cannot be in the future';

const isBlank = value => value === null || value === undefined || String(value).trim() === '';

const tokens = value => String(value).trim().toLowerCase().split(/\s+/);

/**
 * A name field: Unicode letters and the punctuation REGEX_NAME allows.
 *
 * Tested with `REGEX_NAME.test()` rather than through a `regex:` rule string,
 * which is what the Vueform version did and why: a pattern passed as a string
 * loses the `u` flag, degrading `\p{L}` to a literal `p` and rejecting every
 * ordinary letter.
 */
export function validateName(value) {
  if (isBlank(value)) return null;
  return REGEX_NAME.test(String(value)) ? null : ERROR_PATTERN_VALID_NAME;
}

/** Every token of the family name must also appear in the full name. */
export function validateFamilyName(familyName, fullName) {
  if (isBlank(familyName) || isBlank(fullName)) return null;
  const fullTokens = tokens(fullName);
  return tokens(familyName).every(token => fullTokens.includes(token))
    ? null
    : ERROR_VALID_FAMILY_NAME;
}

export function validateEmail(value) {
  if (isBlank(value)) return ERROR_REQUIRED;
  return REGEX_EMAIL.test(String(value).trim()) ? null : ERROR_VALID_EMAIL;
}

/** Optional field; when filled it must be exactly 4 alphanumeric characters. */
export function validatePartialNric(value) {
  if (isBlank(value)) return null;
  return REGEX_NRIC.test(String(value).trim()) ? null : ERROR_VALID_NRIC;
}

/**
 * Date of birth: required, and never in the future. Compared date-only, so
 * "today" is always allowed whatever the time of day.
 */
export function validateDateOfBirth(value, { today = new Date() } = {}) {
  if (isBlank(value)) return ERROR_VALID_DATE;
  const entered = new Date(value);
  if (Number.isNaN(entered.getTime())) return ERROR_VALID_DATE;
  const startOfToday = new Date(today);
  startOfToday.setHours(0, 0, 0, 0);
  entered.setHours(0, 0, 0, 0);
  return entered > startOfToday ? ERROR_DATE_IN_FUTURE : null;
}

/**
 * The typed CG must resolve to an id in the directory — the same lookup
 * `registration/registerUser` uses to build the payload, so the field can only
 * pass when a `cgId` will actually be sent.
 */
export function validateCg(value, cgData) {
  if (isBlank(value)) return ERROR_REQUIRED;
  return resolveCgId(value, cgData) === undefined ? ERROR_GROUP : null;
}

/**
 * Every error on the public registration "about yourself" step, keyed by field.
 * A field with no error is absent from the result.
 */
export function aboutYourselfErrors(form, cgData, options = {}) {
  const errors = {};
  const set = (field, message) => {
    if (message) errors[field] = message;
  };

  set('fullName', isBlank(form.fullName) ? ERROR_REQUIRED : validateName(form.fullName));
  set(
    'familyName',
    isBlank(form.familyName)
      ? ERROR_REQUIRED
      : validateName(form.familyName) || validateFamilyName(form.familyName, form.fullName),
  );
  set('preferredName', validateName(form.preferredName));
  set('dateOfBirth', validateDateOfBirth(form.dateOfBirth, options));
  set('email', validateEmail(form.email));
  if (isBlank(form.gender)) set('gender', ERROR_REQUIRED);
  if (isBlank(form.nationality)) set('nationality', ERROR_REQUIRED);
  // The Connect Group question needs an answer of its own. Leaving it blank
  // used to let the step through as if the member had said No, because the
  // Vueform version only ever consulted the errors of the fields it listed.
  if (isBlank(form.cgExists)) set('cgExists', ERROR_SELECT_OPTION);
  else if (form.cgExists === 'Yes') set('cgId', validateCg(form.cgId, cgData));

  return errors;
}

/** Whether the "about yourself" step may be submitted. */
export function canSubmitAboutYourself(form, cgData, options = {}) {
  return Object.keys(aboutYourselfErrors(form, cgData, options)).length === 0;
}

/**
 * Trim every string in the payload, matching what the Vueform version did with
 * a JSON round-trip (`.replace(/"\s+|\s+"/g, '"')`) — leading and trailing
 * whitespace only, inner spacing untouched.
 */
export function trimFormValues(form) {
  return Object.fromEntries(
    Object.entries(form).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ]),
  );
}
